import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  viewChild,
} from '@angular/core';
import { Modal } from 'flowbite';
import { PublicTransportComparisonScope, UserRole } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';

@Component({
  selector: 'app-modal-edit-town',
  templateUrl: './modal-edit-town.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalEditTownComponent implements OnChanges {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() hidden: boolean = true;
  @Input() loading: boolean = false;
  @Input() userRole: UserRole;
  @Input() town: any = null;
  @Input() clients: any[] = [];
  readonly loadingPercentage = input<number | null>(null);
  readonly loadingDetail = input<string>('');
  @Output() action = new EventEmitter<any>();
  @Output() deleteRequested = new EventEmitter<void>();

  readonly modalElement = viewChild.required<ElementRef<HTMLElement>>('modalElement');

  public images = images;
  public UserRole = UserRole;
  public TooltipPosition = TooltipPosition;

  public name = '';
  public latitude = 0;
  public longitude = 0;
  public timezone = '';
  public whatsapp = false;
  public boNumAlternatives = 0;
  public appNumAlternatives = 0;
  public bookingDaysInAdvance = 0;
  public whatsappNumAlternatives = 0;
  public showStopsListFirst = false;
  public boardingSeconds = 0;
  public boardingPaxSeconds = 0;
  public boardingPrmSeconds = 0;
  public alightingSeconds = 0;
  public alightingPaxSeconds = 0;
  public alightingPrmSeconds = 0;
  public stopTimeMode = 'PARALLEL';
  public dynamicPositioning = false;
  public recurrentBookings = false;
  public correspondences = false;
  public tripPlanner = false;
  public publicTransportComparisonScope: PublicTransportComparisonScope = PublicTransportComparisonScope.NONE;
  public publicTransportNonCompetitionMinutes: number | null = null;
  public publicTransportStopProximityMeters: number | null = null;
  public tripPlannerMaxTransferDistance = 0;
  public tripPlannerEstimateTransferTime = false;
  public initialRecurrentBookings = false;
  public deleteRecurrents = false;
  public confirmingDelete = false;
  public timezones: { value: string; label: string }[] = [];
  public selectedClients: any[] = [];

  constructor(public literalService: LiteralService) {
    const zones: string[] = (Intl as any).supportedValuesOf?.('timeZone') ?? [];
    this.timezones = zones.map((tz) => ({ value: tz, label: tz }));
  }

  onTimezoneChange(event: any) {
    this.timezone = event?.value ?? '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['town'] && this.town) {
      this.confirmingDelete = false;
      const cfg = this.town.config ?? this.town.townConfig ?? {};
      this.name = this.town.name ?? '';
      this.latitude = this.town.latitude ?? 0;
      this.longitude = this.town.longitude ?? 0;
      this.timezone = this.town.timezone ?? '';
      this.whatsapp = !!cfg.whatsapp;
      this.boNumAlternatives = cfg.boNumAlternatives ?? 0;
      this.appNumAlternatives = cfg.appNumAlternatives ?? 0;
      this.bookingDaysInAdvance = cfg.bookingDaysInAdvance ?? 0;
      this.whatsappNumAlternatives = cfg.whatsappNumAlternatives ?? 0;
      this.showStopsListFirst = !!cfg.showStopsListFirst;
      this.boardingSeconds = cfg.boardingSeconds ?? 0;
      this.boardingPaxSeconds = cfg.boardingPaxSeconds ?? 0;
      this.boardingPrmSeconds = cfg.boardingPrmSeconds ?? 0;
      this.alightingSeconds = cfg.alightingSeconds ?? 0;
      this.alightingPaxSeconds = cfg.alightingPaxSeconds ?? 0;
      this.alightingPrmSeconds = cfg.alightingPrmSeconds ?? 0;
      this.stopTimeMode = cfg.stopTimeMode ?? 'PARALLEL';
      this.dynamicPositioning = !!cfg.dynamicPositioning;
      this.recurrentBookings = !!cfg.recurrentBookings;
      this.correspondences = !!cfg.correspondences;
      this.tripPlanner = !!cfg.tripPlanner;
      this.publicTransportComparisonScope = cfg.publicTransportComparisonScope ?? PublicTransportComparisonScope.NONE;
      this.publicTransportNonCompetitionMinutes = cfg.publicTransportNonCompetitionMinutes ?? null;
      this.publicTransportStopProximityMeters = cfg.publicTransportStopProximityMeters ?? null;
      this.tripPlannerMaxTransferDistance = cfg.tripPlannerMaxTransferDistance ?? 0;
      this.tripPlannerEstimateTransferTime = !!cfg.tripPlannerEstimateTransferTime;
      this.initialRecurrentBookings = !!cfg.recurrentBookings;
      this.deleteRecurrents = false;
      const initialClientIds: number[] = this.town.clients ?? [];
      this.selectedClients = this.clients.filter((c: any) =>
        initialClientIds.includes(c.id),
      );
    }
  }

  get canDelete(): boolean {
    return this.userRole === UserRole.SUPERADMIN;
  }

  get canEditClients(): boolean {
    return this.userRole === UserRole.SUPERADMIN;
  }

  close() {
    this.confirmingDelete = false;
    this.modal.hide();
  }

  requestDeleteConfirm() {
    this.confirmingDelete = true;
  }

  cancelDelete() {
    this.confirmingDelete = false;
  }

  confirmDelete() {
    this.deleteRequested.emit();
  }

  onSubmit() {
    this.action.emit({
      name: this.name,
      latitude: this.latitude,
      longitude: this.longitude,
      timezone: this.timezone,
      whatsapp: this.whatsapp,
      boNumAlternatives: this.boNumAlternatives,
      appNumAlternatives: this.appNumAlternatives,
      bookingDaysInAdvance: this.bookingDaysInAdvance,
      whatsappNumAlternatives: this.whatsappNumAlternatives,
      showStopsListFirst: this.showStopsListFirst,
      boardingSeconds: this.boardingSeconds,
      boardingPaxSeconds: this.boardingPaxSeconds,
      boardingPrmSeconds: this.boardingPrmSeconds,
      alightingSeconds: this.alightingSeconds,
      alightingPaxSeconds: this.alightingPaxSeconds,
      alightingPrmSeconds: this.alightingPrmSeconds,
      stopTimeMode: this.stopTimeMode,
      dynamicPositioning: this.dynamicPositioning,
      recurrentBookings: this.recurrentBookings,
      correspondences: this.correspondences,
      tripPlanner: this.tripPlanner,
      publicTransportComparisonScope: this.publicTransportComparisonScope,
      publicTransportNonCompetitionMinutes: this.publicTransportNonCompetitionMinutes,
      publicTransportStopProximityMeters: this.publicTransportStopProximityMeters,
      tripPlannerMaxTransferDistance: this.tripPlannerMaxTransferDistance ?? 0,
      tripPlannerEstimateTransferTime: this.tripPlannerEstimateTransferTime,
      deleteRecurrents: this.deleteRecurrents,
      clients: this.canEditClients
        ? this.selectedClients.map((c: any) => c.id)
        : null,
    });
  }
}
